/* eslint-disable import/no-cycle */
import apisauce from 'apisauce';

import Config from 'config';
import { TokenSession } from 'services/session.service';
import Middleware from './middleware';
import Endpoints from './endpoints';
import { notifyActionFactory } from './utils/promiseHandling';

// Exclude mocks from production build
const Mocks = process.env.NODE_ENV !== 'production' ? require('./mock').default : instance => instance;

export * from './utils/promiseHandling';
export { default as Status } from './utils/status';

let api: any = null;
let apiWithEndPoints: any = null;
let actions: any = null;

/**
 * They are an methods to manage error/response in middleware Api.
 * If exist onSignout in actions, it is executed on received 401 HTTP Error.
 */
interface ConfigProps {
    /**
     * Actions fallbacks in middlware
     */
    actions?: any;
    /**
     * Auth endpoint middlware to refresh token
     */
    refreshTokenUrl?: string;
}

export const create = (config: ConfigProps = {}) => {
    actions = config?.actions;
    api = apisauce.create(Config.api);
    // Manage token
    const token = TokenSession.get();
    if (token) setAuthorizationHeader(token);
    // Create API
    apiWithEndPoints = Endpoints.create(api);
    // Middleware
    api.axiosInstance.interceptors.response.use(
        response => Middleware.responseHandler({ response, ...config }),
        error => Middleware.errorHandler({ error, setAuthorization, ...config })
    );
    // Manage mocks
    if (Config.isTest || Config.useMock) Mocks(api.axiosInstance);

    return apiWithEndPoints;
};

/**
 * Set Auth session
 * @param {Object} token - Token
 * @param {string} token.type - Token type `Bearer`
 * @param {string} token.token - Token value
 */
export const setAuthorization = token => {
    TokenSession.set(token);
    setAuthorizationHeader(token);
};

/**
 * Set Auth Headers
 * @param {Object} token
 * @param {string} token.type - Token type `Bearer`
 * @param {string} token.token - Token value
 */
const setAuthorizationHeader = ({ type, token }) => {
    if (api && type && token) api.setHeaders({ Authorization: `${type} ${token}` });
};

/**
 * Remove Auth Headers
 */
export const removeAuthorization = () => {
    TokenSession.unset();
    if (api) api.deleteHeader('Authorization');
};

/**
 * Notify Actions
 */
export const notifySuccess = (notif?) => notifyActionFactory({ type: 'success', send: actions.notification })(notif);
export const notifyError = (notif?) => notifyActionFactory({ type: 'error', send: actions.notification })(notif);
export const notifyInfo = (notif?) => notifyActionFactory({ type: 'info', send: actions.notification })(notif);
export const notifyHttpError = (notif?) => notifyActionFactory({ error: actions.error })(notif);

/**
 * Instance
 */
export const getApiInstance = () => api;

/**
 * Endpoints
 */
export const Api = () => apiWithEndPoints;

export default {
    create,
    Api,
    getApiInstance,
    setAuthorization,
    removeAuthorization,
};
